<template>
  <b-container class="d-flex flex-column mh-100" fluid>
    <div
      class="pagetitle-container d-flex justify-content-start align-items-center"
    >
      <div class="page-title">
        <h2>
          Prehľad prihlásení
          <small>({{ count }})</small>
        </h2>
      </div>
      <div class="d-flex flex-grow-1">
        <input-filter
          ref="filter"
          :filters="table.filter"
          :options="filterOptions"
          @onReset="resetFilter"
          @onSearch="search"
        >
          <template #filter>
            <b-row>
              <b-col md="4">
                <b-form-group
                  label="Dátum:"
                  label-align-md="right"
                  label-cols-md="4"
                  label-for="date"
                >
                  <b-input-group class="input-group-labeled">
                    <date-picker
                      v-model="table.filter.created_at"
                      class="flex-grow-1 daterange-sm"
                      opens="right"
                      @update="search"
                    />
                    <b-input-group-append>
                      <b-btn
                        size="sm"
                        variant="link"
                        @click="
                          table.filter.created_at = { ge: null, lt: null }
                          search()
                        "
                      >
                        <b-icon icon="x" scale="1.5x" />
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group
                  label="Rola:"
                  label-align-md="right"
                  label-cols-md="4"
                  label-for="role"
                >
                  <b-input-group class="input-group-labeled">
                    <selectbox
                      v-model="table.filter.role"
                      :options="[
                        { id: 1, name: 'Administrátori' },
                        { id: 2, name: 'Užívatelia' },
                      ]"
                      :searchable="false"
                      @input="search"
                    />
                    <b-input-group-append>
                      <b-btn
                        size="sm"
                        variant="link"
                        @click="
                          table.filter.role = null
                          search()
                        "
                      >
                        <b-icon icon="x" scale="1.5x" />
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group
                  label="Akcia:"
                  label-align-md="right"
                  label-cols-md="4"
                  label-for="action"
                >
                  <b-input-group class="input-group-labeled">
                    <selectbox
                      v-model="table.filter.action"
                      :options="[
                        { id: 1, name: 'Prihlásenie' },
                        { id: 0, name: 'Odhlásenie' },
                      ]"
                      :searchable="false"
                      @input="search"
                    />
                    <b-input-group-append>
                      <b-btn
                        size="sm"
                        variant="link"
                        @click="
                          table.filter.action = null
                          search()
                        "
                      >
                        <b-icon icon="x" scale="1.5x" />
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </template>
        </input-filter>
      </div>
      <div v-if="$root.user.isAdmin" class="ml-3">
        <b-dropdown
          class="mr-1"
          menu-class="dropdown-menu-animated"
          right
          size="lg"
          variant="secondary"
        >
          <template #button-content>
            <b-icon icon="gear" />
          </template>
          <b-dropdown-item-button @click="exportLog">
            <b-icon class="mr-1" icon="file-earmark-spreadsheet" />
            Exportovať
          </b-dropdown-item-button>
        </b-dropdown>
      </div>
    </div>
    <div ref="scroll" v-bar class="bar-out">
      <div>
        <server-table
          ref="table"
          :api-request="apiRequest"
          :columns="table.columns"
          :searchable="false"
          :sort-by.sync="table.sortBy"
          :sort-desc="true"
          bordered
          striped
          @refreshed="onLoaded"
        >
          <template #cell(user)="data">
            <template>
              {{ data.item.firstname }} {{ data.item.lastname }}
              <b-badge
                v-if="data.item.role_id === 1"
                variant="primary"
                class="align-top"
              >
                Administrátor
              </b-badge>
            </template>
          </template>
          <template #cell(userAgent)="data">
            <template v-if="data.value">
              {{ data.item.browser }} /
              <small>{{ data.item.platform }}</small>
            </template>
            <template v-else> -</template>
          </template>
          <template #cell(action)="data">
            <b-badge
              v-if="data.item._action && data.item._action.const === 'LOGOUT'"
              variant="danger"
              class="align-top fs-sm"
            >
              Odhlásenie
            </b-badge>
            <b-badge
              v-if="data.item._action && data.item._action.const === 'LOGIN'"
              variant="success"
              class="align-top fs-sm"
            >
              Prihlásenie
            </b-badge>
          </template>
        </server-table>
      </div>
    </div>
  </b-container>
</template>
<script>
import InputFilter from '@/components/InputFilter'
import Selectbox from '@/components/inputs/Selectbox'
import DatePicker from '@/components/inputs/DatePicker'
import ServerTable from '@/components/ServerTable'
import { saveAs } from 'file-saver'

export default {
  components: {
    InputFilter,
    Selectbox,
    DatePicker,
    ServerTable,
  },
  data() {
    return {
      loading: false,
      table: {
        columns: [
          {
            key: 'user',
            label: 'Meno a Priezvisko',
            sortable: true,
            atter: (value, key, item) =>
              item.firstname + ' ' + item.lastname || '-',
            sortableKey: 'firstname, lastname',
            searchable: true,
          },
          {
            key: 'company',
            label: 'Názov spoločnosti',
            formatter: (value, key, item) => item.companyName || '-',
            sortable: true,
            sortableKey: 'companies.name',
            searchable: true,
          },
          {
            key: 'ipAddress',
            label: 'IP adresa',
            formatter: (value, key, item) => item.ipAddress || '-',
            searchable: true,
          },
          {
            key: 'userAgent',
            label: 'Prehliadač',
            formatter: (value, key, item) => item.userAgent || '-',
          },
          {
            key: 'created_at',
            label: 'Dátum a čas',
            formatter: (value) =>
              value ? this.$options.filters.formatDateTime(value) : '-',
            sortable: true,
          },
          {
            key: 'action',
            label: 'Akcia',
          },
        ],
        sortBy: 'created_at',
        filter: {
          created_at: { ge: null, lt: null },
          role: null,
          action: null,
        },
      },
      filterOptions: {
        placeholder: 'Filtrovanie a hľadanie...',
      },
      count: null,
    }
  },
  methods: {
    apiRequest(params) {
      return this.$api.get('users/get_log_list', {
        params: { filter: this.table.filter, ...params },
      })
    },
    onLoaded() {
      this.count = this.$refs.table.totalRows
    },
    search() {
      this.$refs.table.searchVal = this.$refs.filter.value
      this.$nextTick(() => {
        this.$refs.table.search()
      })
    },
    resetFilter() {
      this.$refs.table.reset()
    },
    exportLog() {
      this.$swal({
        toast: true,
        type: 'info',
        position: 'top-end',
        title: 'Pripravujem export...',
      })
      this.$api
        .get('users/export_log', {
          params: {
            query: this.$refs.filter.value,
            filter: this.table.filter,
            ...this.$refs.table.getRequestParams(),
            startAt: 0,
            maxResults: -1,
          },
          responseType: 'blob',
        })
        .then((res) => {
          saveAs(new Blob([res.data]), 'prehlad-prihlaseni.xlsx')
          this.$swal({
            toast: true,
            type: 'success',
            position: 'top-end',
            title: 'Export pripravený',
            timer: 5000,
          })
        })
        .catch(() => {
          this.$swal({
            toast: true,
            type: 'error',
            position: 'top-end',
            title: 'Chyba pri exportovaní',
            timer: 5000,
          })
        })
    },
  },
}
</script>
